import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorIcon from "@mui/icons-material/Error";
import { green, blue, red } from "@mui/material/colors";

// Helper to build mutually exclusive status counts for the funnel
function getExclusiveStatusCounts(row) {
  if (!row) return { sent: 0, delivered: 0, read: 0, failed: 0 };
  const read = row.read_count ?? 0;
  const delivered = (row.delivered_count ?? 0) - read;
  const sent = (row.sent_count ?? 0) - (row.delivered_count ?? 0);
  const failed = row.failed_count ?? 0;
  return {
    sent: sent < 0 ? 0 : sent,
    delivered: delivered < 0 ? 0 : delivered,
    read: read < 0 ? 0 : read,
    failed: failed < 0 ? 0 : failed,
  };
}
// Helper to build donut chart data
function getDonutData(row) {
  const { sent, delivered, read, failed } = getExclusiveStatusCounts(row);
  return [
    { id: 0, value: sent, label: "Sent", color: "#43a047" },
    { id: 1, value: delivered, label: "Delivered", color: "#1976d2" },
    { id: 2, value: read, label: "Read", color: "#0d47a1" },
    { id: 3, value: failed, label: "Failed", color: "#d32f2f" },
  ];
}
const CampaignAnalyticsDialog = ({ open, onClose, analyticsRow }) => {
  const { sent, delivered, read, failed } =
    getExclusiveStatusCounts(analyticsRow);
  const total = sent + delivered + read + failed;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BarChartIcon color="primary" />
          Campaign Analytics
        </Box>
      </DialogTitle>
      <DialogContent>
        {analyticsRow && (
          <>
            {/* Donut Chart */}
            <Box mb={3} display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap={3}>
              {/* Donut Chart */}
              <PieChart
                series={[
                  {
                    data: getDonutData(analyticsRow),
                    innerRadius: 50,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    cx: 80,
                    cy: 80,
                    arcLabel: (item) =>
                      item.value > 0 ? `${item.value}` : "",
                    arcLabelMinAngle: 20,
                  },
                ]}
                width={180}
                height={180}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fontSize: 12,
                    fontWeight: 500,
                  },
                }}
                legend={{ hidden: true }}
              />
              {/* Custom Legend */}
              <Box display="flex" flexDirection="column" gap={1}>
                {getDonutData(analyticsRow).map((item) => (
                  <Box key={item.id} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 16, height: 16, bgcolor: item.color, borderRadius: '4px', mr: 1 }} />
                    <Typography variant="body2" color="text.primary">{item.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" mt={1} textAlign="center">
              WhatsApp Status Distribution
            </Typography>

            {/* Status Cards with Icons and Percentages */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="subtitle2">Total Batches</Typography>
                  <Typography variant="h6" color="text.primary">
                    {analyticsRow.total_batches ?? 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="subtitle2">Current Batch</Typography>
                  <Typography variant="h6" color="text.primary">
                    {analyticsRow.current_batch ?? 0} /{" "}
                    {analyticsRow.total_batches ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignAnalyticsDialog;
